import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { usePatchCvMutation } from "../../../lib/cvsSlice";
import { useCvStepNavigation } from "../../../contexts/CvStepNavigationContext";

const portfolioRegex = /^https?:\/\/[a-zA-Z0-9.-]+(\.[a-zA-Z]{2,}){1,}([\/\S]*)?$/;

const parseLinks = (links, portfolio) => {
  if (Array.isArray(links) && links.length) return links;
  if (typeof portfolio === "string") {
    const parts = portfolio.split(";").filter(Boolean);
    if (parts.length) return parts;
  }
  return [""];
};

const Portfolio = ({ portfolio, links, cvId }) => {
  const [port, setPort] = useState(() => parseLinks(links, portfolio));
  const [isSaving, setIsSaving] = useState(false);
  const [patchCv] = usePatchCvMutation();
  const { goToNextStep, goToPrevStep } = useCvStepNavigation();

  useEffect(() => {
    setPort(parseLinks(links, portfolio));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cvId]);

  const handleChange = (event, index) => {
    const newPort = [...port];
    newPort[index] = event.target.value;
    setPort(newPort);
  };

  const handleAdd = () => {
    setPort([...port, ""]);
  };

  const handleRemove = (index) => {
    const newPort = [...port];
    newPort.splice(index, 1);
    setPort(newPort);
  };

  const save = async () => {
    const validLinks = port.filter((p) => p.trim());
    const invalid = validLinks.find((p) => !portfolioRegex.test(p));
    if (invalid !== undefined) {
      toast.error("Invalid link. Please enter a valid URL starting with http:// or https://");
      return false;
    }
    await patchCv({ cvId, links: validLinks }).unwrap();
    return true;
  };

  const handleSaveAndContinueLater = async () => {
    setIsSaving(true);
    try {
      await save();
    } catch (err) {
      toast.error(`Failed to save: ${err?.data?.message ?? err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    setIsSaving(true);
    try {
      const ok = await save();
      if (ok) goToNextStep();
    } catch (err) {
      toast.error(`Failed to save: ${err?.data?.message ?? err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Link fields */}
      {port.map((value, index) => (
        <TextField
          key={index}
          label={`Link ${index + 1}`}
          value={value}
          onChange={(e) => handleChange(e, index)}
          fullWidth
          variant="outlined"
          placeholder="https://"
          autoComplete="off"
          InputProps={{
            endAdornment: index > 0 && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Remove link"
                  onClick={() => handleRemove(index)}
                  size="small"
                  sx={{ color: "#9E9E9E", "&:hover": { color: "#E84610" } }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      ))}

      {/* Add link */}
      <Link
        component="button"
        variant="body2"
        onClick={handleAdd}
        sx={{
          textTransform: "uppercase",
          textDecoration: "none",
          fontWeight: 600,
          fontSize: "13px",
          alignSelf: "flex-start",
          color: "#1A1A1A",
          cursor: "pointer",
          letterSpacing: "0.5px",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        + Add Another Link
      </Link>

      {/* Actions */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
        <Button
          variant="outlined"
          onClick={goToPrevStep}
          disabled={isSaving}
          sx={{
            borderColor: "#C5BFBE",
            color: "#3D3634",
            borderRadius: "6px",
            px: 3,
            fontWeight: 600,
            letterSpacing: "0.5px",
            "&:hover": { borderColor: "#9E9E9E", backgroundColor: "#F4F2F1" },
          }}
        >
          BACK
        </Button>

        <Link
          component="button"
          variant="body2"
          onClick={handleSaveAndContinueLater}
          disabled={isSaving}
          sx={{
            textDecoration: "none",
            color: "#7A706F",
            cursor: "pointer",
            fontSize: "13px",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Save &amp; Continue Later
        </Link>

        <Button
          variant="contained"
          onClick={handleNext}
          disabled={isSaving}
          sx={{
            backgroundColor: "#E84610",
            "&:hover": { backgroundColor: "#C73A0D" },
            "&:disabled": { backgroundColor: "#E8A090" },
            borderRadius: "6px",
            px: 3,
            fontWeight: 600,
            letterSpacing: "0.5px",
          }}
        >
          {isSaving ? "Saving..." : "NEXT"}
        </Button>
      </Box>
    </Box>
  );
};

export default Portfolio;
